import React from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, H3, Paragraph, Separator } from '@fs/zion-ui'
import PersonNameCheckbox from './PersonNameCheckbox'
import { filterPersonsByName } from './filterPersonsByName'

/**
 * Renders the selectable names grouped under a heading per image.
 *
 * Within an image the names keep the groups they already belong to, ruled off from one another.
 * Filtering narrows the visible list only — `selectedPersonIds` is owned by the caller, so a person
 * filtered out of view keeps their selection.
 *
 * @param {Object} params
 * @param {Array<import('./usePersonsByImage').ImageNameGroup>} params.imageGroups - Groups to render, in ribbon order
 * @param {Array<string>} [params.selectedPersonIds=[]] - Currently selected person ids
 * @param {string} [params.filterValue=''] - Filter text; narrows display only, never selection
 * @param {(personId: string) => void} [params.onToggle] - Called with the person id when a row is toggled
 * @param {Array<string>} [params.disabledPersonIds=[]] - Person ids rendered but not selectable
 * @returns {React.ReactElement}
 */
const PersonsByImageCheckboxes = ({
  imageGroups,
  selectedPersonIds = [],
  filterValue = '',
  onToggle = () => {},
  disabledPersonIds = [],
}) => {
  const [t] = useTranslation()
  const selectedIdSet = new Set(selectedPersonIds)
  const disabledIdSet = new Set(disabledPersonIds)

  return (
    <>
      {imageGroups.map((group, index) => {
        const visibleGroups = group.personGroups
          .map((personGroup) => ({
            ...personGroup,
            persons: filterPersonsByName({ persons: personGroup.persons, filterValue }),
          }))
          .filter((personGroup) => personGroup.persons.length > 0)

        return (
          <React.Fragment key={group.ark}>
            {index > 0 && <Separator size="xs" />}
            <H3 size="H6">
              {t('add-existing-person.names-on-image.heading', 'Names on image {imageNumber}', {
                imageNumber: group.imageNumber,
              })}
            </H3>
            <Divider marginY="nano" />
            {group.personGroups.length === 0 && (
              <Paragraph size="sm" secondary>
                {t('add-existing-person.no-names-on-image', 'No names on this image.')}
              </Paragraph>
            )}
            {group.personGroups.length > 0 && visibleGroups.length === 0 && (
              <Paragraph size="sm" secondary>
                {t('add-existing-person.no-names-match-filter', 'No names match your filter.')}
              </Paragraph>
            )}
            {visibleGroups.map((personGroup) => (
              <React.Fragment key={personGroup.id}>
                {personGroup.persons.map((person) => (
                  <PersonNameCheckbox
                    key={person.id}
                    person={person}
                    checked={selectedIdSet.has(person.id)}
                    disabled={disabledIdSet.has(person.id)}
                    onToggle={onToggle}
                  />
                ))}
                <Divider marginY="nano" />
              </React.Fragment>
            ))}
          </React.Fragment>
        )
      })}
    </>
  )
}

export default PersonsByImageCheckboxes
